import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { Loader2, Warehouse, Wrench, Archive, ArrowRightLeft, RotateCcw, Fingerprint, Search, CalendarClock, Printer, X, StickyNote, Download, ScanLine, Package } from "lucide-react";
import Header from "@/components/Header";
import { useLang } from "@/i18n";
import { useConfirm } from "@/components/ConfirmDialog";
import api, { apiError } from "@/lib/api";
import { fmtDateOnly } from "@/lib/time";
import { downloadBlob } from "@/lib/export";
import LabelSheet from "@/components/LabelSheet";
import { ProductsPanel } from "@/pages/Products";

const CONDITION_STYLES = {
  neu: "text-[#c00000] bg-[#c00000]/10 border-[#c00000]/30",
  gebraucht: "text-[#c00000] bg-[#c00000]/10 border-[#c00000]/30",
  defekt: "text-red-700 bg-red-500/10 border-red-500/30",
  ausgemustert: "text-gray-600 bg-gray-50 border-gray-200",
};

const TABS = [
  { id: "lager", de: "Lager", en: "Stock", icon: Warehouse },
  { id: "defekt", de: "Defekt", en: "Defective", icon: Wrench },
  { id: "ausgemustert", de: "Ausgemustert", en: "Retired", icon: Archive },
];

const daysBetween = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return Math.max(0, Math.floor((Date.now() - d.getTime()) / 86400000));
};

function LabelSheetX() { return null; }

function AssignDialog({ device, codes, onCancel, onConfirm, de, busy }) {
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onCancel} data-testid="assign-dialog">
      <div className="bg-gray-50 border border-gray-200 rounded-xl p-5 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 mb-1"><ArrowRightLeft className="w-5 h-5 text-[#c00000]" /><h3 className="text-gray-900 font-semibold">{de ? "Station zuweisen" : "Assign to station"}</h3></div>
        <p className="text-xs text-gray-600 mb-4">{de ? `Gerät ${device.asset_tag} einer Station zuweisen.` : `Assign ${device.asset_tag} to a station.`}</p>
        <label className="block text-xs text-gray-600 mb-1">{de ? "Zielstation (Geräte-Code)" : "Target station code"}</label>
        <input
          list="station-codes"
          data-testid="assign-code"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder="z. B. FRAT03-01"
          autoFocus
          className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-800 focus:border-[#c00000] outline-none mb-1"
        />
        <datalist id="station-codes">{codes.map((c) => <option key={c} value={c} />)}</datalist>
        <p className="text-[11px] text-gray-600 mb-3">{de ? "Tippen zum Filtern bekannter Stationen — oder neuen Code eingeben." : "Type to filter known stations — or enter a new code."}</p>
        <label className="block text-xs text-gray-600 mb-1">{de ? "Standort-Name (optional)" : "Location name (optional)"}</label>
        <input
          data-testid="assign-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-800 focus:border-[#c00000] outline-none mb-4"
        />
        <div className="flex justify-end gap-2">
          <button onClick={onCancel} className="px-3 py-2 rounded-lg text-sm text-gray-600 hover:text-gray-900">{de ? "Abbrechen" : "Cancel"}</button>
          <button data-testid="assign-confirm" disabled={busy || !code.trim()} onClick={() => onConfirm(code, name)} className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm bg-[#c00000]/20 border border-[#c00000]/40 text-[#c00000] hover:bg-[#c00000]/30 disabled:opacity-40">
            <ArrowRightLeft className="w-4 h-4" /> {de ? "Zuweisen" : "Assign"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Inventory() {
  const { lang } = useLang();
  const de = lang === "de";
  const confirm = useConfirm();
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("lager");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("recent");
  const [sel, setSel] = useState(() => new Set());
  const [sheetOpen, setSheetOpen] = useState(false);
  const [assignFor, setAssignFor] = useState(null);
  const [stationCodes, setStationCodes] = useState([]);
  const [kind, setKind] = useState("device");
  const [view, setView] = useState("katalog");
  const [scanners, setScanners] = useState([]);
  const toggleSel = (tag) => setSel((p) => { const n = new Set(p); n.has(tag) ? n.delete(tag) : n.add(tag); return n; });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [inv, scn] = await Promise.all([api.get("/inventory"), api.get("/scanners")]);
      setItems(inv.data || []);
      setScanners((scn.data || []).filter((s) => ["lager", "defekt", "ausgemustert"].includes(s.status)));
    } catch (err) { toast.error(apiError(err)); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const act = async (fn) => { setBusy("x"); try { await fn(); await load(); } catch (err) { toast.error(apiError(err)); } finally { setBusy(""); } };
  const cancelLabel = de ? "Abbrechen" : "Cancel";

  const openAssign = async (d) => {
    setAssignFor(d);
    if (stationCodes.length === 0) {
      try {
        const r = await api.get("/devices");
        const codes = [...new Set((r.data || []).map((x) => x.device_code).filter(Boolean))].sort();
        setStationCodes(codes);
      } catch { /* datalist bleibt leer, freie Eingabe moeglich */ }
    }
  };
  const doAssign = async (code, name) => {
    const target = (code || "").trim();
    if (!target) { toast.error(de ? "Zielstation erforderlich" : "Target station required"); return; }
    setBusy("x");
    try {
      if (kind === "scanner") {
        await api.post(`/scanners/${encodeURIComponent(assignFor.asset_tag)}/assign`, { device_code: target });
      } else {
        await api.post(`/devices/${encodeURIComponent(assignFor.asset_tag)}/assign`, { device_code: target, location_name: name || "" });
      }
      toast.success(`${assignFor.asset_tag} → ${target}`);
      setAssignFor(null);
      await load();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(""); }
  };

  const setCondition = (d, condition) => act(async () => {
    const retire = condition === "ausgemustert";
    if (!(await confirm({
      title: retire ? (de ? "Endgültig ausmustern" : "Retire permanently") : (de ? "Als defekt einstufen" : "Mark defective"),
      message: retire
        ? (de ? `Wollen Sie „${d.asset_tag}“ wirklich endgültig ausmustern? 100% defekt, nicht reparierbar.` : `Really retire "${d.asset_tag}" permanently?`)
        : (de ? `Wollen Sie „${d.asset_tag}“ wirklich als defekt einstufen? (Reparaturchance besteht)` : `Really mark "${d.asset_tag}" as defective?`),
      confirmLabel: retire ? (de ? "Ausmustern" : "Retire") : (de ? "Als defekt" : "Mark defective"),
      cancelLabel, danger: retire,
    }))) throw new Error("cancel");
    const note = window.prompt(
      retire ? (de ? "Grund / Notiz zur Ausmusterung (optional):" : "Reason / note for retirement (optional):")
             : (de ? "Defektgrund / Notiz (optional):" : "Defect reason / note (optional):"),
      d.note || ""
    );
    if (note === null) throw new Error("cancel");
    await api.post(`/devices/${encodeURIComponent(d.asset_tag)}/condition`, { condition, note });
    toast.success(`${d.asset_tag}: ${condition}`);
  });

  const backToLager = (d) => act(async () => {
    if (!(await confirm({
      title: de ? "Zurück ins Lager" : "Back to stock",
      message: de ? `Wollen Sie „${d.asset_tag}“ wirklich zurück ins Lager legen?` : `Really move "${d.asset_tag}" back to stock?`,
      confirmLabel: de ? "Ins Lager" : "To stock", cancelLabel,
    }))) throw new Error("cancel");
    await api.post(`/devices/${encodeURIComponent(d.asset_tag)}/lager`, { condition: "gebraucht" });
    toast.success(`${d.asset_tag} → ${de ? "Lager" : "Stock"}`);
  });

  const scToLager = (s) => act(async () => {
    if (!(await confirm({ title: de ? "Zurück ins Lager" : "Back to stock", message: de ? `Scanner „${s.asset_tag}“ (${s.scanner_serial}) zurück ins Lager?` : `Move scanner "${s.asset_tag}" back to stock?`, confirmLabel: de ? "Ins Lager" : "To stock", cancelLabel }))) throw new Error("cancel");
    await api.post(`/scanners/${encodeURIComponent(s.asset_tag)}/lager`, { condition: "gebraucht" });
    toast.success(`${s.asset_tag} → ${de ? "Lager" : "Stock"}`);
  });
  const scCondition = (s, condition) => act(async () => {
    const retire = condition === "ausgemustert";
    if (!(await confirm({ title: retire ? (de ? "Scanner ausmustern" : "Retire scanner") : (de ? "Scanner als defekt" : "Mark scanner defective"), message: de ? `Scanner „${s.asset_tag}“ (${s.scanner_serial}) als ${condition} markieren?` : `Mark scanner "${s.asset_tag}" as ${condition}?`, confirmLabel: retire ? (de ? "Ausmustern" : "Retire") : (de ? "Als defekt" : "Mark defective"), cancelLabel, danger: retire }))) throw new Error("cancel");
    await api.post(`/scanners/${encodeURIComponent(s.asset_tag)}/condition`, { condition });
    toast.success(`${s.asset_tag}: ${condition}`);
  });

  const isDev = kind === "device";
  const rawList = isDev ? items : scanners;
  const catField = isDev ? "assignment" : "status";
  const ql = q.trim().toLowerCase();
  let shown = rawList.filter((d) => d[catField] === tab);
  if (ql) shown = shown.filter((d) => (isDev
      ? [d.asset_tag, d.former_code, d.teamviewer_id, d.device_uid]
      : [d.asset_tag, d.scanner_serial, d.former_device, d.current_device_code, d.scanner_type]
    ).some((v) => String(v || "").toLowerCase().includes(ql)));
  shown = [...shown].sort((a, b) => {
    if (sort === "tag") return String(a.asset_tag || "").localeCompare(String(b.asset_tag || ""));
    const key = isDev ? "assignment_at" : "last_seen";
    const ta = new Date(a[key] || 0).getTime();
    const tb = new Date(b[key] || 0).getTime();
    return sort === "oldest" ? ta - tb : tb - ta;
  });
  const sinceLabel = de
    ? (tab === "lager" ? "im Lager seit " : tab === "defekt" ? "defekt seit " : "ausgemustert seit ")
    : "since ";
  const counts = { lager: 0, defekt: 0, ausgemustert: 0 };
  rawList.forEach((d) => { const c = d[catField]; if (counts[c] !== undefined) counts[c]++; });
  const shownTags = shown.map((d) => d.asset_tag);
  const allSelected = shownTags.length > 0 && shownTags.every((t) => sel.has(t));
  const toggleAll = () => setSel((p) => { const n = new Set(p); if (allSelected) shownTags.forEach((t) => n.delete(t)); else shownTags.forEach((t) => n.add(t)); return n; });
  const selectedDevices = [...items, ...scanners].filter((d) => sel.has(d.asset_tag));

  const exportCsv = () => {
    const esc = (v) => { const s = v == null ? "" : String(v); return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
    let header, rows, fname;
    if (kind === "scanner") {
      header = ["AssetID", "Zustand", "Kategorie", "Seriennummer", "Typ", "Letztes Tablet"];
      rows = shown.map((d) => [d.asset_tag, d.condition || "", d.status || "", d.scanner_serial || "", d.scanner_type || "", d.former_device || d.current_device_code || ""].map(esc).join(";"));
      fname = `lager-scanner-${tab}-${new Date().toISOString().slice(0, 10)}.csv`;
    } else {
      header = ["AssetID", "Zustand", "Kategorie", "Letzter Code", "TeamViewer-ID", "Geraete-UID", "Eingelagert am", "Tage", "Notiz"];
      rows = shown.map((d) => [
        d.asset_tag, d.condition || "", d.assignment || "", d.former_code || "", d.teamviewer_id || "", d.device_uid || "",
        d.assignment_at ? fmtDateOnly(d.assignment_at) : "", daysBetween(d.assignment_at) ?? "", d.note || "",
      ].map(esc).join(";"));
      fname = `lager-${tab}-${new Date().toISOString().slice(0, 10)}.csv`;
    }
    const csv = "\uFEFF" + [header.join(";"), ...rows].join("\n");
    downloadBlob(csv, fname, "text/csv;charset=utf-8");
  };

  return (
    <div className="min-h-screen bg-white text-gray-800">
      <Header />
      <div className="w-full px-4 sm:px-6 py-6">
        <div className="flex items-center gap-3 mb-1">
          <Warehouse className="w-6 h-6 text-[#c00000]" />
          <h1 className="text-2xl font-bold text-gray-900">{de ? "Lager / Inventar" : "Stock / Inventory"}</h1>
        </div>
        <p className="text-sm text-gray-600 mb-4">{de ? "Geräte & Scanner ohne Station — mit QR-Etikett zur eindeutigen Wiedererkennung." : "Devices & scanners without a station — with QR label for identification."}</p>

        <div className="inline-flex rounded-lg border border-gray-200 p-1 mb-4 bg-gray-50">
          <button data-testid="inv-view-katalog" onClick={() => setView("katalog")} className={`px-3 py-1.5 rounded-md text-sm flex items-center gap-2 transition-colors ${view === "katalog" ? "bg-gray-100 text-gray-900" : "text-gray-600 hover:text-gray-900"}`}>
            <Package className="w-4 h-4" /> {de ? "Katalog & Bestand" : "Catalog & stock"}
          </button>
          <button data-testid="inv-view-units" onClick={() => setView("units")} className={`px-3 py-1.5 rounded-md text-sm flex items-center gap-2 transition-colors ${view === "units" ? "bg-gray-100 text-gray-900" : "text-gray-600 hover:text-gray-900"}`}>
            <Warehouse className="w-4 h-4" /> {de ? "Einzelstücke & Etiketten" : "Units & labels"}
          </button>
        </div>

        {view === "katalog" ? <ProductsPanel /> : (<>
        <div className="inline-flex rounded-lg border border-gray-200 p-1 mb-4 bg-gray-50">
          <button data-testid="inv-kind-device" onClick={() => { setKind("device"); setSel(new Set()); }} className={`px-3 py-1.5 rounded-md text-sm flex items-center gap-2 transition-colors ${isDev ? "bg-gray-100 text-gray-900" : "text-gray-600 hover:text-gray-900"}`}>
            <Warehouse className="w-4 h-4" /> {de ? "Geräte / PCs" : "Devices / PCs"}
          </button>
          <button data-testid="inv-kind-scanner" onClick={() => { setKind("scanner"); setSel(new Set()); }} className={`px-3 py-1.5 rounded-md text-sm flex items-center gap-2 transition-colors ${kind === "scanner" ? "bg-gray-100 text-gray-900" : "text-gray-600 hover:text-gray-900"}`}>
            <ScanLine className="w-4 h-4" /> Scanner
          </button>
        </div>

        <div className="flex flex-wrap gap-2 mb-6">
          {TABS.map((tt) => {
            const Icon = tt.icon;
            return (
              <button
                key={tt.id}
                data-testid={`inv-tab-${tt.id}`}
                onClick={() => setTab(tt.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm border transition-colors ${
                  tab === tt.id ? "bg-gray-100 border-gray-300 text-gray-900" : "bg-gray-50 border-gray-200 text-gray-600 hover:text-gray-900"
                }`}
              >
                <Icon className="w-4 h-4" /> {de ? tt.de : tt.en} <span className="text-gray-600">({counts[tt.id]})</span>
              </button>
            );
          })}
        </div>

        <div className="flex flex-col sm:flex-row gap-2 mb-5">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-gray-600 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              data-testid="inv-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={de ? "Suche: AssetID, letzter Code, TV-ID…" : "Search: AssetID, last code, TV-ID…"}
              className="w-full bg-gray-50 border border-gray-200 rounded-lg pl-9 pr-3 py-2.5 text-sm text-gray-800 focus:border-[#c00000] outline-none"
            />
          </div>
          <select
            data-testid="inv-sort"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-700 focus:border-[#c00000] outline-none"
          >
            <option value="recent">{de ? "Zuletzt eingelagert" : "Recently stocked"}</option>
            <option value="oldest">{de ? "Am längsten im Lager" : "Longest in stock"}</option>
            <option value="tag">AssetID</option>
          </select>
          <button
            data-testid="inv-select-all"
            onClick={toggleAll}
            disabled={shownTags.length === 0}
            className="shrink-0 rounded-lg px-3 py-2.5 text-sm border bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 disabled:opacity-40"
          >
            {allSelected ? (de ? "Auswahl aufheben" : "Clear") : (de ? "Alle wählen" : "Select all")}
          </button>
          <button
            data-testid="inv-print-labels"
            onClick={() => setSheetOpen(true)}
            disabled={sel.size === 0}
            className="shrink-0 flex items-center justify-center gap-2 rounded-lg px-3 py-2.5 text-sm border bg-[#c00000]/15 border-[#c00000]/30 text-[#c00000] hover:bg-[#c00000]/25 disabled:opacity-40"
          >
            <Printer className="w-4 h-4" /> {de ? "Etiketten" : "Labels"}{sel.size ? ` (${sel.size})` : ""}
          </button>
          <button
            data-testid="inv-export-csv"
            onClick={exportCsv}
            disabled={shown.length === 0}
            className="shrink-0 flex items-center justify-center gap-2 rounded-lg px-3 py-2.5 text-sm border bg-gray-50 border-gray-200 text-gray-800 hover:bg-gray-100 disabled:opacity-40"
          >
            <Download className="w-4 h-4" /> CSV
          </button>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-gray-600"><Loader2 className="w-4 h-4 animate-spin" /> {de ? "Lädt…" : "Loading…"}</div>
        ) : shown.length === 0 ? (
          <div className="text-gray-600 text-sm py-10 text-center" data-testid="inv-empty">{de ? "Keine Geräte in dieser Kategorie." : "No devices in this category."}</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="inventory-grid">
            {shown.map((d) => (
              <div key={d.asset_tag} data-testid={`inv-card-${d.asset_tag}`} className="relative bg-gray-50 border border-gray-200 rounded-xl p-4 flex gap-4">
                <input
                  type="checkbox"
                  data-testid={`inv-select-${d.asset_tag}`}
                  checked={sel.has(d.asset_tag)}
                  onChange={() => toggleSel(d.asset_tag)}
                  className="absolute top-3 right-3 accent-[#c00000] w-4 h-4 cursor-pointer"
                />
                <div className="bg-white rounded-lg p-2 shrink-0 self-start">
                  <QRCodeSVG value={isDev ? `${window.location.origin}/d/${encodeURIComponent(d.asset_tag || d.device_code || d.device_uid || "")}` : (d.asset_tag || "")} size={84} level="M" />
                </div>
                {isDev ? (
                <div className="min-w-0 flex-1">
                  <div className="font-mono text-base font-bold text-gray-900">{d.asset_tag}</div>
                  <span className={`inline-block mt-1 text-[10px] font-mono uppercase px-1.5 py-0.5 rounded border ${CONDITION_STYLES[d.condition] || CONDITION_STYLES.gebraucht}`}>{d.condition || "gebraucht"}</span>
                  <div className="mt-2 space-y-0.5 text-xs text-gray-600 font-mono break-all">
                    <div>{de ? "zuletzt" : "last"}: {d.former_code || "—"}</div>
                    <div className="flex items-center gap-1"><Fingerprint className="w-3 h-3" /> {d.device_uid ? d.device_uid.slice(0, 18) : "—"}</div>
                    {d.teamviewer_id && <div>TV: {d.teamviewer_id}</div>}
                  </div>
                  {d.assignment_at && (
                    <div className="mt-2 flex items-center gap-1 text-[11px] text-gray-600 font-mono" data-testid={`inv-since-${d.asset_tag}`}>
                      <CalendarClock className="w-3 h-3 shrink-0" />
                      {sinceLabel}{fmtDateOnly(d.assignment_at)}
                      {daysBetween(d.assignment_at) !== null && ` · ${daysBetween(d.assignment_at)} ${de ? "T" : "d"}`}
                    </div>
                  )}
                  {d.note && (
                    <div className="mt-1.5 flex items-start gap-1 text-[11px] text-amber-700/90 break-words" data-testid={`inv-note-${d.asset_tag}`}>
                      <StickyNote className="w-3 h-3 mt-0.5 shrink-0" /> {d.note}
                    </div>
                  )}
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {tab === "lager" && (
                      <>
                        <button data-testid={`inv-assign-${d.asset_tag}`} disabled={!!busy} onClick={() => openAssign(d)} className="flex items-center gap-1 text-xs bg-[#c00000]/15 border border-[#c00000]/30 text-[#c00000] rounded px-2 py-1 hover:bg-[#c00000]/25 disabled:opacity-50">
                          <ArrowRightLeft className="w-3 h-3" /> {de ? "Zuweisen" : "Assign"}
                        </button>
                        <button disabled={!!busy} onClick={() => setCondition(d, "defekt")} className="text-xs bg-gray-50 border border-gray-200 text-gray-700 rounded px-2 py-1 hover:bg-gray-100 disabled:opacity-50">{de ? "Defekt" : "Defective"}</button>
                        <button disabled={!!busy} onClick={() => setCondition(d, "ausgemustert")} className="text-xs bg-gray-50 border border-gray-200 text-gray-700 rounded px-2 py-1 hover:bg-gray-100 disabled:opacity-50">{de ? "Ausmustern" : "Retire"}</button>
                      </>
                    )}
                    {(tab === "defekt" || tab === "ausgemustert") && (
                      <button data-testid={`inv-back-${d.asset_tag}`} disabled={!!busy} onClick={() => backToLager(d)} className="flex items-center gap-1 text-xs bg-[#c00000]/15 border border-[#c00000]/30 text-[#c00000] rounded px-2 py-1 hover:bg-[#c00000]/25 disabled:opacity-50">
                        <RotateCcw className="w-3 h-3" /> {de ? "Zurück ins Lager" : "Back to stock"}
                      </button>
                    )}
                  </div>
                </div>
                ) : (
                <div className="min-w-0 flex-1">
                  <div className="font-mono text-base font-bold text-gray-900">{d.asset_tag}</div>
                  <span className={`inline-block mt-1 text-[10px] font-mono uppercase px-1.5 py-0.5 rounded border ${CONDITION_STYLES[d.condition] || CONDITION_STYLES.gebraucht}`}>{d.condition || "gebraucht"}</span>
                  <div className="mt-2 space-y-0.5 text-xs text-gray-600 font-mono break-all">
                    <div className="flex items-center gap-1"><ScanLine className="w-3 h-3" /> {d.scanner_serial || "—"}</div>
                    <div>{d.scanner_type || "—"}</div>
                    <div>{de ? "zuletzt" : "last"}: {d.former_device || d.current_device_code || "—"}</div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {tab === "lager" && (
                      <>
                        <button data-testid={`inv-assign-${d.asset_tag}`} disabled={!!busy} onClick={() => openAssign(d)} className="flex items-center gap-1 text-xs bg-[#c00000]/15 border border-[#c00000]/30 text-[#c00000] rounded px-2 py-1 hover:bg-[#c00000]/25 disabled:opacity-50">
                          <ArrowRightLeft className="w-3 h-3" /> {de ? "Zuweisen" : "Assign"}
                        </button>
                        <button disabled={!!busy} onClick={() => scCondition(d, "defekt")} className="text-xs bg-gray-50 border border-gray-200 text-gray-700 rounded px-2 py-1 hover:bg-gray-100 disabled:opacity-50">{de ? "Defekt" : "Defective"}</button>
                        <button disabled={!!busy} onClick={() => scCondition(d, "ausgemustert")} className="text-xs bg-gray-50 border border-gray-200 text-gray-700 rounded px-2 py-1 hover:bg-gray-100 disabled:opacity-50">{de ? "Ausmustern" : "Retire"}</button>
                      </>
                    )}
                    {(tab === "defekt" || tab === "ausgemustert") && (
                      <button data-testid={`inv-back-${d.asset_tag}`} disabled={!!busy} onClick={() => scToLager(d)} className="flex items-center gap-1 text-xs bg-[#c00000]/15 border border-[#c00000]/30 text-[#c00000] rounded px-2 py-1 hover:bg-[#c00000]/25 disabled:opacity-50">
                        <RotateCcw className="w-3 h-3" /> {de ? "Zurück ins Lager" : "Back to stock"}
                      </button>
                    )}
                  </div>
                </div>
                )}
              </div>
            ))}
          </div>
        )}
        </>)}
      </div>
      {sheetOpen && <LabelSheet devices={selectedDevices} onClose={() => setSheetOpen(false)} de={de} />}
      {assignFor && <AssignDialog device={assignFor} codes={stationCodes} busy={!!busy} onCancel={() => setAssignFor(null)} onConfirm={doAssign} de={de} />}
    </div>
  );
}
